import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog.tsx';
import { Button } from './ui/button.tsx';
import { Label } from './ui/label.tsx';
import { toast } from 'sonner';
import { Copy } from 'lucide-react';
import { createLogger } from '@metron/shared';
import { copyToClipboard } from '../lib/clipboard.ts';
import type {
  DataLinkAccount,
  ResetDataLinkCredentialsResponse,
} from '../hooks/useDataLinkAccounts.ts';

const logger = createLogger('ResetDataLinkCredentialsDialog');

interface ResetDataLinkCredentialsDialogProps {
  account: DataLinkAccount | null;
  onOpenChange: (open: boolean) => void;
  onReset: (username: string) => Promise<ResetDataLinkCredentialsResponse>;
}

export function ResetDataLinkCredentialsDialog({
  account,
  onOpenChange,
  onReset,
}: ResetDataLinkCredentialsDialogProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<ResetDataLinkCredentialsResponse | null>(null);

  const handleReset = async () => {
    if (!account) return;

    setIsLoading(true);
    try {
      const response = await onReset(account.username);
      setResult(response);
      if (response.emailSent) {
        toast.success('Credentials reset and onboarding email sent');
      } else {
        toast.warning(
          'Credentials reset, but the email failed to send — share the temporary password manually',
        );
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to reset credentials';
      toast.error(errorMessage);
      logger.error({ error }, 'Failed to reset DataLink credentials');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setResult(null);
    onOpenChange(false);
  };

  return (
    <Dialog open={account !== null} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className='sm:max-w-[600px]'>
        {!result
          ? (
            <>
              <DialogHeader>
                <DialogTitle>Reset DataLink Credentials</DialogTitle>
                <DialogDescription>
                  This will invalidate the current password for{' '}
                  <span className='font-medium'>{account?.email}</span>{' '}
                  and email them a new temporary password. They must change it on next login. This
                  cannot be undone.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button
                  type='button'
                  variant='outline'
                  onClick={handleClose}
                  disabled={isLoading}
                >
                  Cancel
                </Button>
                <Button
                  type='button'
                  variant='destructive'
                  onClick={handleReset}
                  disabled={isLoading}
                >
                  {isLoading ? 'Resetting...' : 'Reset Credentials'}
                </Button>
              </DialogFooter>
            </>
          )
          : (
            <>
              <DialogHeader>
                <DialogTitle>Credentials Reset</DialogTitle>
                <DialogDescription>
                  {result.emailSent
                    ? 'A new temporary password was emailed to the customer. It is also shown below as a backup.'
                    : 'The email failed to send. Copy the credentials below and share them with the customer.'}
                </DialogDescription>
              </DialogHeader>
              <div className='grid gap-4 py-4'>
                <div className='grid gap-2'>
                  <Label>Username (Email)</Label>
                  <div className='flex items-center gap-2'>
                    <div className='flex-1 p-3 bg-muted rounded-md font-mono text-sm break-all'>
                      {result.email}
                    </div>
                    <Button
                      type='button'
                      variant='outline'
                      size='icon'
                      aria-label='Copy username'
                      onClick={() => copyToClipboard(result.email, 'Username')}
                    >
                      <Copy className='h-4 w-4' />
                    </Button>
                  </div>
                </div>
                <div className='grid gap-2'>
                  <Label>Temporary Password</Label>
                  <div className='flex items-center gap-2'>
                    <div className='flex-1 p-3 bg-muted rounded-md font-mono text-sm break-all'>
                      {result.temporaryPassword}
                    </div>
                    <Button
                      type='button'
                      variant='outline'
                      size='icon'
                      aria-label='Copy temporary password'
                      onClick={() =>
                        copyToClipboard(result.temporaryPassword, 'Temporary password')}
                    >
                      <Copy className='h-4 w-4' />
                    </Button>
                  </div>
                </div>
                {result.emailError && (
                  <p className='text-sm text-destructive'>
                    Email error: {result.emailError}
                  </p>
                )}
              </div>
              <DialogFooter>
                <Button onClick={handleClose}>Done</Button>
              </DialogFooter>
            </>
          )}
      </DialogContent>
    </Dialog>
  );
}
